import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import ConsentBanner from "@/components/ConsentBanner";
import Layout from "@/components/layout/Layout";
import { RequireAuth, RequireRole } from "@/components/layout/RouteGuards";
import Home from "@/pages/Home";
import Explore from "@/pages/Explore";
import Answers from "@/pages/Answers";
import Privacy from "@/pages/Privacy";
import LearnerView from "@/pages/LearnerView";
import PathView from "@/pages/PathView";
import Settings from "@/pages/Settings";
import AiKeys from "@/pages/AiKeys";
import KGCuratorView from "@/pages/KGCuratorView";
import AdminView from "@/pages/AdminView";
import { Toaster } from "@/components/ui/sonner";

function AppRoutes() {
  // OAuth returns to "/" with a #session_id=… hash — complete sign-in first.
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/explore" element={<Explore />} />
        <Route path="/answers" element={<Answers />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route
          path="/journey"
          element={
            <RequireAuth>
              <LearnerView />
            </RequireAuth>
          }
        />
        <Route
          path="/journey/path/:trackId"
          element={
            <RequireAuth>
              <PathView />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="/ai-keys"
          element={
            <RequireAuth>
              <AiKeys />
            </RequireAuth>
          }
        />
        <Route
          path="/curate"
          element={
            <RequireRole roles={["curator", "admin"]}>
              <KGCuratorView />
            </RequireRole>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireRole roles={["admin"]}>
              <AdminView />
            </RequireRole>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function PageviewTracker() {
  const location = useLocation();
  const posthog = usePostHog();
  React.useEffect(() => {
    posthog?.capture("$pageview");
  }, [location.pathname, location.search, posthog]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PageviewTracker />
          <AppRoutes />
          <ConsentBanner />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
